/**
 * Route guard — redirects unauthorized users to access-denied or default route.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getRoutePermission, getDefaultRoute } from '../../../shared/routePermissions';
import type { PermissionAction, PermissionCategory } from '../../../shared/types/User';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Override auto-detected permission from pathname */
  category?: PermissionCategory;
  action?: PermissionAction;
  roomId?: string;
}

export function ProtectedRoute({ children, category, action, roomId }: ProtectedRouteProps) {
  const { can, hasControllerAccess, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !hasControllerAccess) {
    return <Navigate to="/access-denied" replace state={{ from: location.pathname }} />;
  }

  const routePerm = getRoutePermission(location.pathname);
  const requiredCategory = category ?? routePerm?.category;
  const requiredAction = action ?? routePerm?.action ?? 'view';

  if (requiredCategory && !can(requiredCategory, requiredAction, roomId)) {
    const fallback = getDefaultRoute(can);
    if (fallback === '/access-denied') {
      return <Navigate to="/access-denied" replace state={{ from: location.pathname, required: routePerm?.label }} />;
    }
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
